import { type ReactNode, createContext, useMemo } from 'react'

import {
  type SanityFrameBuilderConfiguration,
  type SanityFrameBuilderType,
} from '@frame-builder/data/sanity/queries/types'
import { type SanityFrameBuilderServicePrices } from '@frame-builder/data/sanity/queries/types/service-prices'
import { type SanityFrameBuilderSettings } from '@frame-builder/data/sanity/queries/types/settings'
import {
  getMaximumProfileHeight,
  getMaximumProfileWidth,
  getMinimumProfileSize,
} from '@frame-builder/lib/size'
import {
  type FrameBuilderConfigurationWithLimits,
  type FrameBuilderLimits,
} from '@frame-builder/lib/types'

interface FrameBuilderSettingsContextProps {
  frameBuilderType: SanityFrameBuilderType
  frameBuilderSettings: SanityFrameBuilderSettings
  frameBuilderConfiguration: FrameBuilderConfigurationWithLimits
}

interface FrameBuilderSettingsContextProviderProps {
  frameBuilderType: SanityFrameBuilderType
  configuration: SanityFrameBuilderConfiguration
  children: ReactNode
}

const emptySettings: SanityFrameBuilderSettings = {
  minimumPassepartoutSize: 0,
  maximumPassepartoutSize: 0,
}

const emptyServicePrices: SanityFrameBuilderServicePrices = {
  backBoard: 0,
  backBoardMinimum: 0,
  cutWaste: 0,
  largeFrameProductionFee: 0,
  productionFee: 0,
  customPassepartoutProductionFee: 0,
  customPassepartoutPriceMultiplier: 1,
}

const emptyFrameBuilderConfigurationWithLimits: FrameBuilderConfigurationWithLimits =
  {
    profiles: [],
    textures: [],
    profileTextures: [],
    passepartoutColors: [],
    glass: [],
    spacerSizes: [],
    spacerColors: [],
    servicePrices: emptyServicePrices,
    standardSizes: [],
    limits: {
      minimumSize: 1,
      maximumWidth: 1000,
      maximumHeight: 1000,
    },
  }

export const FrameBuilderSettingsContext =
  createContext<FrameBuilderSettingsContextProps>({
    frameBuilderType: 'frame',
    frameBuilderSettings: emptySettings,
    frameBuilderConfiguration: emptyFrameBuilderConfigurationWithLimits,
  })

export const FrameBuilderSettingsContextProvider = ({
  frameBuilderType,
  configuration,
  children,
}: FrameBuilderSettingsContextProviderProps) => {
  const frameBuilderSettings = useMemo(() => {
    const strings: SanityFrameBuilderSettings = {
      ...emptySettings,
      ...(configuration.settings ?? {}),
    }

    return strings
  }, [configuration.settings])

  const frameBuilderConfiguration = useMemo(() => {
    const minimumSize = getMinimumProfileSize(configuration.profiles)
    const maximumWidth = getMaximumProfileWidth(configuration.profiles)
    const maximumHeight = getMaximumProfileHeight(configuration.profiles)
    const limits: FrameBuilderLimits = {
      minimumSize,
      maximumWidth,
      maximumHeight,
    }

    const frameBuilderConfiguration: FrameBuilderConfigurationWithLimits = {
      profiles: configuration.profiles,
      textures: configuration.textures,
      profileTextures: configuration.profileTextures,
      passepartoutColors: configuration.passepartoutColors,
      glass: configuration.glass,
      spacerSizes: configuration.spacerSizes,
      spacerColors: configuration.spacerColors,
      servicePrices: configuration.servicePrices ?? emptyServicePrices,
      standardSizes: configuration.standardSizes,
      limits,
    }

    return frameBuilderConfiguration
  }, [configuration])

  return (
    <FrameBuilderSettingsContext.Provider
      value={{
        frameBuilderType,
        frameBuilderSettings,
        frameBuilderConfiguration,
      }}
    >
      {children}
    </FrameBuilderSettingsContext.Provider>
  )
}
